/* TransferDetailsDialog & TransferEntriesViewer
 * ─────────────────────────────────────────────
 * Reusable dialogs that bolt onto the existing FinancialManagement transfer
 * workflow without altering current balances/UI structure.
 *
 * 1. BalanceDetailsDialog — shown when user clicks "تفاصيل الرصيد" before
 *    transferring. Lists every transferable financial entry comprising the
 *    transferable balance with filters (date / employee / op-type / receipt
 *    search). User selects entries; on submit, parent calls the enhanced
 *    `cash-transfer-with-entries` endpoint which locks those entries.
 *
 * 2. TransferEntriesDialog — shown on existing transfer rows that have
 *    `linked_entry_ids`. Lists the receipts attached to that specific
 *    transfer. Includes a "Print Detail" button that fetches the HTML
 *    detailed report and prints it.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const fmtDT = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('ar-DZ', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(v); }
};
const fmtMoney = (n) =>
  `${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const OP_TYPE_LABEL = {
  daily_operation:  'عملية يومية',
  invoice_payment:  'دفع فاتورة',
  ticket_sale:      'بيع تذكرة',
  trip_sale:        'بيع رحلة',
  payment:          'دفعة',
  expense:          'مصروف',
  refund:           'استرجاع',
  other:            'أخرى',
};
const PM_LABEL = {
  cash: 'نقدًا', bank: 'بنكي', card: 'بطاقة', cheque: 'شيك',
};

/* ════════════════════════════════════════════════════════════════════════
   BalanceDetailsDialog
   ════════════════════════════════════════════════════════════════════════ */
export const BalanceDetailsDialog = ({
  open,
  onClose,
  agencyId,
  initialRecipient = '',
  initialNotes = '',
  onTransferred,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [transferableTotal, setTransferableTotal] = useState(0);
  // 🆕 Categorised deductions since the last transfer for this agency
  const [deductions, setDeductions] = useState({
    items: [],
    refunds: 0, discounts: 0, other: 0, total: 0, net: 0,
  });
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    start_date: '', end_date: '', employee_id: '', operation_type: '', search: '',
  });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [recipient, setRecipient] = useState(initialRecipient);
  const [notes, setNotes] = useState(initialNotes);
  // 🔍 Diagnostic panel state
  const [reconLoading, setReconLoading] = useState(false);
  const [recon, setRecon] = useState(null);
  const [fixing, setFixing] = useState(false);

  const load = useCallback(async (override = {}) => {
    if (!agencyId) return;
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: 1, per_page: 200 });
      const merged = { ...filters, ...override };
      if (merged.start_date) p.set('start_date', new Date(merged.start_date).toISOString());
      if (merged.end_date)   p.set('end_date',   new Date(merged.end_date + 'T23:59:59').toISOString());
      if (merged.employee_id)    p.set('employee_id', merged.employee_id);
      if (merged.operation_type) p.set('operation_type', merged.operation_type);
      if (merged.search)         p.set('search', merged.search);
      const r = await axios.get(
        `${API}/agencies/${agencyId}/transferable-entries?${p}`,
        { headers: authHeader() },
      );
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setTransferableTotal(r.data.transferable_total || 0);
      setEmployees(r.data.employees || []);
      // 🆕 Deductions summary since last transfer
      setDeductions({
        items: r.data.deductions || [],
        refunds: r.data.deductions_refunds || 0,
        discounts: r.data.deductions_discounts || 0,
        other: r.data.deductions_other || 0,
        total: r.data.deductions_total || 0,
        net: r.data.net_transferable || 0,
      });
    } catch (e) {
      toast.error('فشل تحميل تفاصيل الرصيد: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [agencyId, filters]);

  const runReconciliation = useCallback(async () => {
    if (!agencyId) return;
    setReconLoading(true);
    try {
      const r = await axios.get(
        `${API}/agencies/${agencyId}/balance-reconciliation`,
        { headers: authHeader() },
      );
      setRecon(r.data);
    } catch (e) {
      toast.error('تعذّر إجراء الفحص: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setReconLoading(false);
    }
  }, [agencyId]);

  const [fixResult, setFixResult] = useState(null);
  const fixOrphans = useCallback(async () => {
    if (!agencyId) return;
    setFixing(true);
    setFixResult(null);
    try {
      console.log('[fix-orphans] calling endpoint for agency:', agencyId);
      const r = await axios.post(
        `${API}/agencies/${agencyId}/fix-orphan-payments`,
        {},
        { headers: authHeader() },
      );
      console.log('[fix-orphans] response:', r.data);
      setFixResult({ ok: true, data: r.data });
      await runReconciliation();
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'خطأ غير معروف';
      console.error('[fix-orphans] error:', e?.response || e);
      setFixResult({ ok: false, error: detail, status: e?.response?.status });
      toast.error('فشل الإصلاح: ' + detail);
    } finally {
      setFixing(false);
    }
  }, [agencyId, runReconciliation, load]);

  const [forcing, setForcing] = useState(false);
  const [perRowBusy, setPerRowBusy] = useState(null); // payment_id of busy row

  const addEntryForPayment = useCallback(async (paymentId) => {
    if (!paymentId) return;
    setPerRowBusy(paymentId);
    try {
      const r = await axios.post(
        `${API}/payments/${paymentId}/create-financial-entry`,
        {},
        { headers: authHeader() },
      );
      if (r.data?.already_existed) {
        toast.info('ℹ️ الوصل موجود مسبقاً — تم تحديث القائمة');
      } else {
        toast.success(r.data?.message || '✅ تم إنشاء الوصل');
      }
      await runReconciliation();
      load();
    } catch (e) {
      toast.error('فشل إنشاء الوصل: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setPerRowBusy(null);
    }
  }, [runReconciliation, load]);

  const forceFix = useCallback(async () => {
    if (!agencyId) return;
    setForcing(true);
    setFixResult(null);
    try {
      const r = await axios.post(
        `${API}/agencies/${agencyId}/fix-orphan-payments-force`,
        {},
        { headers: authHeader() },
      );
      setFixResult({ ok: true, data: r.data, mode: 'force' });
      await runReconciliation();
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'خطأ غير معروف';
      console.error('[force-fix] error:', e?.response || e);
      setFixResult({ ok: false, error: detail, status: e?.response?.status, mode: 'force' });
      toast.error('فشل الإصلاح القوي: ' + detail);
    } finally {
      setForcing(false);
    }
  }, [agencyId, runReconciliation, load]);

  const [cleaning, setCleaning] = useState(false);
  const cleanupSpurious = useCallback(async () => {
    if (!agencyId) return;
    setCleaning(true);
    setFixResult(null);
    try {
      const r = await axios.post(
        `${API}/agencies/${agencyId}/cleanup-spurious-entries`,
        {},
        { headers: authHeader() },
      );
      setFixResult({ ok: true, data: r.data, mode: 'cleanup' });
      await runReconciliation();
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'خطأ غير معروف';
      console.error('[cleanup] error:', e?.response || e);
      setFixResult({ ok: false, error: detail, status: e?.response?.status, mode: 'cleanup' });
      toast.error('فشل التنظيف: ' + detail);
    } finally {
      setCleaning(false);
    }
  }, [agencyId, runReconciliation, load]);

  // When the dialog opens, reset selection / form fields and pull entries.
  // We deliberately do NOT include `load`, `initialRecipient`, `initialNotes`
  // here — they would re-trigger the side-effect mid-interaction and reset
  // the user's selection. `open` flips false→true on each dialog opening,
  // which is the only trigger we want.
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setRecipient(initialRecipient);
      setNotes(initialNotes);
      load();
    }
  }, [open, agencyId, initialRecipient, initialNotes, load]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => setSelectedIds(new Set(items.map((e) => e.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const selected = useMemo(
    () => items.filter((e) => selectedIds.has(e.id)),
    [items, selectedIds],
  );
  const selectedTotal = selected.reduce((s, e) => s + Number(e.amount || 0), 0);

  const submitTransfer = async () => {
    if (!selectedIds.size) {
      toast.error('يرجى اختيار وصل واحد على الأقل');
      return;
    }
    if (!recipient.trim()) {
      toast.error('يرجى إدخال اسم المُستلِم');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/agencies/${agencyId}/cash-transfer-with-entries`,
        {
          entry_ids: Array.from(selectedIds),
          recipient_name: recipient.trim(),
          notes,
        },
        { headers: authHeader() },
      );
      toast.success(
        `✅ تم إنشاء التحويل ${r.data.reference_no} — ${fmtMoney(r.data.transfer.linked_total)} دج (${r.data.locked_entries} وصل)`,
      );
      onTransferred && onTransferred(r.data);
      onClose();
    } catch (e) {
      toast.error('فشل التحويل: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 flex-wrap">
            <span>💰 تفاصيل الرصيد القابل للتحويل</span>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
                {total} وصل — {fmtMoney(transferableTotal)} دج
              </Badge>
              {deductions.total > 0 && (
                <>
                  <Badge className="bg-rose-100 text-rose-800 border border-rose-300" data-testid="bd-deductions-badge">
                    خصومات: -{fmtMoney(deductions.total)} دج
                  </Badge>
                  <Badge className={`border ${deductions.net >= 0 ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-red-100 text-red-800 border-red-300'}`} data-testid="bd-net-badge">
                    الصافي: {fmtMoney(deductions.net)} دج
                  </Badge>
                </>
              )}
              <button
                type="button"
                onClick={runReconciliation}
                disabled={reconLoading}
                className="text-xs font-bold px-3 py-1.5 rounded-md border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-700 disabled:opacity-60"
                data-testid="bd-run-reconcile"
                title="فحص أين تختبئ الفروقات بين الرصيد والوصولات"
              >
                {reconLoading ? '⏳ جاري الفحص…' : '🔍 فحص الفرق'}
              </button>
              <button
                type="button"
                onClick={() => {
                  // 🆕 Open the printable net-balance statement in a new
                  //     tab. The endpoint returns HTML with an embedded
                  //     print button and window.print() on load, so the
                  //     accountant can hand a signed paper copy to the
                  //     manager before every transfer.
                  const token = localStorage.getItem('token');
                  fetch(`${API}/agencies/${agencyId}/net-balance-statement/print`, {
                    headers: { Authorization: `Bearer ${token}` },
                  })
                    .then((r) => r.text())
                    .then((html) => {
                      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
                      const blobUrl = URL.createObjectURL(blob);
                      window.open(blobUrl, '_blank');
                    })
                    .catch((e) => alert('فشل التحميل: ' + e.message));
                }}
                className="text-xs font-bold px-3 py-1.5 rounded-md border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800"
                data-testid="bd-print-net-balance"
                title="طباعة كشف رسمي لصافي الرصيد قبل التحويل"
              >
                🖨️ طباعة كشف الصافي
              </button>
            </div>
          </DialogTitle>
          <DialogDescription>
            اختر الوصولات المراد تحويلها. سيتم قفل الوصولات المُحدَّدة ومنع تعديلها بعد إنشاء التحويل.
          </DialogDescription>
        </DialogHeader>

        {/* 🆕 Deductions summary card — shows the refunds + discounts +
            other cashbox expenses that happened AFTER the last transfer,
            so the manager can reconcile "الرصيد القابل للتحويل vs النقد
            الفعلي في الخزينة". Collapsible: expanded by default when
            deductions exist, hidden entirely when total=0. */}
        {deductions.total > 0 && (
          <div
            className="bg-gradient-to-l from-rose-50 to-orange-50 border-2 border-rose-200 rounded-xl p-4 space-y-3"
            data-testid="bd-deductions-section"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="font-bold text-rose-900 text-base">
                💸 خصومات على رصيد الوكالة منذ آخر تحويل ({deductions.items.length})
              </h3>
              <div className="text-xs text-gray-600">
                هذه المبالغ قد خصمت من الخزينة بعد آخر تحويل — الصافي المتاح للتحويل الفعلي:{' '}
                <span className={`font-bold ${deductions.net >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                  {fmtMoney(deductions.net)} دج
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="bg-white/70 rounded-md p-2 border border-rose-200" data-testid="bd-ded-refunds">
                <div className="text-xs text-gray-600">↩️ الاسترجاعات</div>
                <div className="text-base font-bold text-rose-700">
                  -{fmtMoney(deductions.refunds)} دج
                </div>
              </div>
              <div className="bg-white/70 rounded-md p-2 border border-amber-200" data-testid="bd-ded-discounts">
                <div className="text-xs text-gray-600">💸 سندات التخفيض</div>
                <div className="text-base font-bold text-amber-800">
                  -{fmtMoney(deductions.discounts)} دج
                </div>
              </div>
              <div className="bg-white/70 rounded-md p-2 border border-orange-200" data-testid="bd-ded-other">
                <div className="text-xs text-gray-600">📦 مصاريف أخرى</div>
                <div className="text-base font-bold text-orange-800">
                  -{fmtMoney(deductions.other)} دج
                </div>
              </div>
              <div className={`rounded-md p-2 border font-bold ${deductions.net >= 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`} data-testid="bd-ded-net">
                <div className="text-xs text-gray-600">💰 الصافي</div>
                <div className={`text-base ${deductions.net >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                  {fmtMoney(deductions.net)} دج
                </div>
              </div>
            </div>

            {/* Compact deductions table (top 10) */}
            <div className="max-h-56 overflow-y-auto border rounded-md bg-white">
              <table className="w-full text-xs">
                <thead className="bg-rose-100 text-rose-900 sticky top-0">
                  <tr>
                    <th className="p-1.5 text-start">التاريخ</th>
                    <th className="p-1.5 text-start">النوع</th>
                    <th className="p-1.5 text-start">الوصف</th>
                    <th className="p-1.5 text-start">المرجع</th>
                    <th className="p-1.5 text-end">المبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  {deductions.items.slice(0, 30).map((d) => {
                    const catLabel = {
                      refund: '↩️ استرجاع',
                      discount: '💸 تخفيض',
                      other: '📦 مصروف',
                    }[d.category] || d.category;
                    const ref = d.refund_operation_no || d.source_ticket_no
                      || d.source_operation_no || d.invoice_number || '—';
                    return (
                      <tr key={d.id} className="border-t hover:bg-rose-50/40" data-testid={`bd-ded-row-${d.id}`}>
                        <td className="p-1.5 whitespace-nowrap text-[10px] text-gray-600">
                          {d.created_at ? new Date(d.created_at).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="p-1.5">{catLabel}</td>
                        <td className="p-1.5 max-w-xs truncate" title={d.description}>
                          {d.description || '—'}
                        </td>
                        <td className="p-1.5 font-mono text-[10px]">{ref}</td>
                        <td className="p-1.5 text-end font-bold text-rose-700 whitespace-nowrap">
                          -{fmtMoney(d.amount)}
                        </td>
                      </tr>
                    );
                  })}
                  {deductions.items.length > 30 && (
                    <tr>
                      <td colSpan={5} className="p-2 text-center text-gray-500 text-[10px]">
                        …و {deductions.items.length - 30} خصم إضافي
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 🔍 Reconciliation diagnostic panel */}
        {recon && (
          <div
            className="bg-rose-50 border-2 border-rose-200 rounded-xl p-4 space-y-3 text-sm"
            data-testid="bd-recon-panel"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h3 className="font-bold text-rose-900 text-base">
                🔍 تقرير فحص الفرق
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                {/* 🔧 Always-visible Fix button — runs the manual backfill
                    even when no orphan sample is shown (covers the case
                    where the gap comes from non-«معتمد» ops the lazy
                    backfill silently skipped). Idempotent and safe. */}
                <button
                  type="button"
                  onClick={fixOrphans}
                  disabled={fixing || cleaning || forcing}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-bold px-3 py-1.5 rounded-md"
                  data-testid="bd-fix-orphans-top"
                  title="إنشاء الوصولات المفقودة لكل دفعات الوكالة"
                >
                  {fixing ? '⏳ جاري الإصلاح…' : '🔧 إصلاح الوصولات المفقودة'}
                </button>
                {/* 🧹 Cleanup — only useful when gap is NEGATIVE (transferable > balance) */}
                <button
                  type="button"
                  onClick={cleanupSpurious}
                  disabled={fixing || cleaning || forcing}
                  className="bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-xs font-bold px-3 py-1.5 rounded-md"
                  data-testid="bd-cleanup-spurious"
                  title="حذف الوصولات الزائدة (المرتبطة بعمليات غير معتمدة)"
                >
                  {cleaning ? '⏳ جاري التنظيف…' : '🧹 تنظيف الوصولات الزائدة'}
                </button>
                {/* 🔨 Force Fix — bypasses op-status filter. Use when the
                    regular fix returns 0 yet orphans are reported. */}
                <button
                  type="button"
                  onClick={forceFix}
                  disabled={fixing || cleaning || forcing}
                  className="bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white text-xs font-bold px-3 py-1.5 rounded-md"
                  data-testid="bd-force-fix"
                  title="إصلاح قوي — يتجاوز فلتر «معتمد» على العملية المصدر"
                >
                  {forcing ? '⏳ جاري الإصلاح القوي…' : '🔨 إصلاح قوي'}
                </button>
                <button
                  type="button"
                  onClick={() => setRecon(null)}
                  className="text-xs text-rose-700 hover:underline"
                >
                  ✕ إغلاق
                </button>
              </div>
            </div>

            {/* 📨 Inline response panel — shows immediately after Fix/Cleanup
                so the user has clear visual feedback even when toasts are
                blocked or the result is `0 created`. */}
            {fixResult && (
              <div
                className={`rounded-md p-3 text-sm border ${fixResult.ok ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : 'bg-rose-100 border-rose-400 text-rose-900'}`}
                data-testid="bd-fix-result"
              >
                {fixResult.ok ? (
                  <div>
                    <div className="font-bold">
                      {fixResult.mode === 'cleanup'
                        ? `🧹 تم حذف ${fixResult.data?.deleted || 0} وصلاً زائداً بقيمة ${Number(fixResult.data?.amount || 0).toLocaleString()} دج`
                        : `✅ تم إنشاء ${fixResult.data?.created || 0} وصلاً جديداً بقيمة ${Number(fixResult.data?.amount || 0).toLocaleString()} دج`}
                    </div>
                    {fixResult.data?.errors?.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-rose-700 font-bold">
                          ⚠️ فشلت {fixResult.data.errors.length} عملية — اضغط للتفاصيل
                        </summary>
                        <pre className="mt-1 bg-white p-2 rounded text-[10px] text-rose-900 max-h-48 overflow-auto whitespace-pre-wrap">
                          {JSON.stringify(fixResult.data.errors, null, 2)}
                        </pre>
                      </details>
                    )}
                    {fixResult.data?.created === 0 && !fixResult.data?.errors?.length && fixResult.mode !== 'cleanup' && (
                      <div className="mt-2 text-xs text-amber-800">
                        💡 لم يتم إنشاء أي وصل جديد — كل الدفعات على عمليات معتمدة لها وصولات مسبقاً.
                        قد يكون السبب: دفعات على عمليات بحالة غير «معتمد» (انتظار، مسودة، مرفوضة).
                        تواصل مع المطور لتشخيص أعمق.
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="font-bold">❌ فشل الطلب{fixResult.status ? ` (HTTP ${fixResult.status})` : ''}</div>
                    <div className="text-xs mt-1 font-mono break-all">{fixResult.error}</div>
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="bg-white border rounded p-2">
                <div className="text-[11px] text-gray-500">الرصيد الحالي</div>
                <div className="font-bold text-gray-900">
                  {fmtMoney(recon.summary.current_balance)}
                </div>
              </div>
              <div className="bg-white border rounded p-2">
                <div className="text-[11px] text-gray-500">القابل للتحويل</div>
                <div className="font-bold text-gray-900">
                  {fmtMoney(recon.summary.transferable_total_view)}
                </div>
              </div>
              <div className={`border rounded p-2 ${Math.abs(recon.summary.gap) < 0.01 ? 'bg-emerald-50 border-emerald-300' : 'bg-rose-100 border-rose-400'}`}>
                <div className="text-[11px] text-gray-500">الفرق</div>
                <div className={`font-bold ${Math.abs(recon.summary.gap) < 0.01 ? 'text-emerald-700' : 'text-rose-800'}`}>
                  {fmtMoney(recon.summary.gap)} دج
                </div>
              </div>
              <div className="bg-white border rounded p-2">
                <div className="text-[11px] text-gray-500">دفعات بدون وصل</div>
                <div className={`font-bold ${recon.orphan_payments.total > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
                  {fmtMoney(recon.orphan_payments.total)} دج
                </div>
              </div>
            </div>

            {recon.diagnosis.length > 0 && (
              <div className="space-y-2">
                <div className="font-semibold text-rose-900">⚠️ الأسباب المُحتمَلة</div>
                {recon.diagnosis.map((d, i) => (
                  <div key={i} className="bg-white border-r-4 border-rose-400 rounded p-2">
                    <div className="font-bold text-rose-800">
                      {d.label} — {fmtMoney(d.amount)} دج
                    </div>
                    <div className="text-xs text-gray-700 mt-1">{d.hint}</div>
                  </div>
                ))}
              </div>
            )}

            {recon.orphan_payments.sample.length > 0 && (
              <details className="bg-white border rounded p-2" open>
                <summary className="cursor-pointer font-semibold text-amber-800 text-xs">
                  📋 عيّنة من الدفعات بدون وصل ({recon.orphan_payments.count}) — اضغط للعرض
                </summary>
                <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs text-gray-600">
                    هذه الدفعات تظهر في الرصيد لكن لم تُسجَّل كـ «وصولات قابلة للتحويل».
                    اضغط الزر لإصلاح المشكلة فوراً.
                  </p>
                  <button
                    type="button"
                    onClick={fixOrphans}
                    disabled={fixing}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-bold px-3 py-1.5 rounded-md"
                    data-testid="bd-fix-orphans"
                  >
                    {fixing ? '⏳ جاري الإصلاح…' : '🔧 إصلاح تلقائي'}
                  </button>
                </div>
                <table className="w-full mt-2 text-xs">
                  <thead>
                    <tr className="bg-amber-50">
                      <th className="p-1 text-right">رقم الدفعة</th>
                      <th className="p-1 text-right">العميل</th>
                      <th className="p-1 text-right">المبلغ</th>
                      <th className="p-1 text-right">الطريقة</th>
                      <th className="p-1 text-right">حالة العملية</th>
                      <th className="p-1 text-right">الإجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recon.orphan_payments.sample.map((p) => {
                      const approved = !!p.op_is_approved || p.op_status === 'معتمد';
                      return (
                        <tr key={p.payment_id} className="border-b">
                          <td className="p-1 font-mono">{p.payment_no || p.payment_id?.slice(0, 8)}</td>
                          <td className="p-1">{p.client_name || '—'}</td>
                          <td className="p-1 font-semibold">{fmtMoney(p.amount)}</td>
                          <td className="p-1">{p.payment_method}</td>
                          <td className="p-1">
                            {approved ? (
                              <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                                ✅ معتمدة
                              </span>
                            ) : (
                              <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold">
                                ⏳ {p.op_status || 'غير معتمدة'}
                              </span>
                            )}
                          </td>
                          <td className="p-1">
                            {approved ? (
                              <button
                                type="button"
                                onClick={() => addEntryForPayment(p.payment_id)}
                                disabled={perRowBusy === p.payment_id}
                                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-[10px] font-bold px-2 py-1 rounded"
                                data-testid={`bd-add-entry-${p.payment_id}`}
                              >
                                {perRowBusy === p.payment_id ? '⏳…' : '➕ إضافة وصل'}
                              </button>
                            ) : (
                              <span
                                className="text-[10px] text-amber-700"
                                title="على المحاسب اعتماد العملية أولاً"
                              >
                                🔒 تحتاج اعتماد المحاسب
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </details>
            )}

            {/* 🔒 Locked entries hint */}
            {recon.financial_entries.locked_amount > 0 && (
              <div className="bg-white border-r-4 border-amber-500 rounded p-2">
                <div className="font-bold text-amber-800 text-sm">
                  🔒 وصولات مقفولة في تحويل قيد التأكيد:&nbsp;
                  {fmtMoney(recon.financial_entries.locked_amount)} دج
                </div>
                <p className="text-xs text-gray-700 mt-1">
                  هذه الوصولات مرتبطة بتحويلات معلَّقة (pending). لتحريرها: افتح
                  «📋 سجل التحويلات» في صفحة الحالة المالية واضغط ✅ لتأكيدها
                  أو ❌ لرفضها وإعادة المبلغ للرصيد القابل للتحويل.
                </p>
              </div>
            )}

            <details className="bg-white border rounded p-2">
              <summary className="cursor-pointer font-semibold text-gray-700 text-xs">
                🔬 تفاصيل تقنية (للمحاسب)
              </summary>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-gray-500">payments_total:</span> {fmtMoney(recon.summary.payments_total)}</div>
                <div><span className="text-gray-500">payments_count:</span> {recon.summary.payments_count}</div>
                <div><span className="text-gray-500">confirmed_tx:</span> {fmtMoney(recon.summary.confirmed_transfers)}</div>
                <div><span className="text-gray-500">pending_tx:</span> {fmtMoney(recon.summary.pending_transfers)}</div>
                <div><span className="text-gray-500">expenses:</span> {fmtMoney(recon.summary.expenses)}</div>
                <div><span className="text-gray-500">FE locked:</span> {fmtMoney(recon.financial_entries.locked_amount)}</div>
                <div><span className="text-gray-500">FE non-cash:</span> {fmtMoney(recon.financial_entries.non_cash_amount)}</div>
                <div><span className="text-gray-500">FE count:</span> {recon.financial_entries.count}</div>
              </div>
              <div className="mt-2">
                <div className="text-gray-500 mb-1">FE by status:</div>
                {Object.entries(recon.financial_entries.by_status || {}).map(([k, v]) => (
                  <span key={k} className="inline-block px-2 py-0.5 mr-1 mb-1 bg-gray-100 rounded">
                    {k}: {fmtMoney(v)}
                  </span>
                ))}
              </div>
              <div className="mt-2">
                <div className="text-gray-500 mb-1">FE by source:</div>
                {Object.entries(recon.financial_entries.by_source_collection || {}).map(([k, v]) => (
                  <span key={k} className="inline-block px-2 py-0.5 mr-1 mb-1 bg-gray-100 rounded">
                    {k}: {fmtMoney(v)}
                  </span>
                ))}
              </div>
              <div className="mt-2">
                <div className="text-gray-500 mb-1">Payments by method:</div>
                {Object.entries(recon.payments_by_method || {}).map(([k, v]) => (
                  <span key={k} className="inline-block px-2 py-0.5 mr-1 mb-1 bg-gray-100 rounded">
                    {k}: {fmtMoney(v)}
                  </span>
                ))}
              </div>
            </details>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 bg-amber-50/40 border border-amber-200 rounded-xl p-3">
          <div>
            <Label className="text-xs">بحث (رقم الوصل / وصف / عميل)</Label>
            <Input value={filters.search}
                   onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                   onKeyDown={(e) => e.key === 'Enter' && load()}
                   placeholder="FIN-..." data-testid="bd-filter-search" />
          </div>
          <div>
            <Label className="text-xs">من تاريخ العملية</Label>
            <Input type="date" value={filters.start_date}
                   onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                   data-testid="bd-filter-start" />
          </div>
          <div>
            <Label className="text-xs">إلى تاريخ العملية</Label>
            <Input type="date" value={filters.end_date}
                   onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                   data-testid="bd-filter-end" />
          </div>
          <div>
            <Label className="text-xs">الموظف</Label>
            <Select value={filters.employee_id || 'all'}
                    onValueChange={(v) => setFilters({ ...filters, employee_id: v === 'all' ? '' : v })}>
              <SelectTrigger data-testid="bd-filter-employee"><SelectValue placeholder="الكل" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">الكل</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">نوع العملية</Label>
            <Select value={filters.operation_type || 'all'}
                    onValueChange={(v) => setFilters({ ...filters, operation_type: v === 'all' ? '' : v })}>
              <SelectTrigger data-testid="bd-filter-op-type"><SelectValue placeholder="الكل" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">الكل</SelectItem>
                {Object.entries(OP_TYPE_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 md:col-span-5 flex gap-2">
            <Button onClick={() => load()} className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="bd-filter-apply">
              🔍 تطبيق الفلاتر
            </Button>
            <Button variant="outline" onClick={() => {
              const cleared = { start_date: '', end_date: '', employee_id: '', operation_type: '', search: '' };
              setFilters(cleared);
              setTimeout(() => load(cleared), 50);
            }}>↺ مسح</Button>
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={selectAllVisible}
                    data-testid="bd-select-all">تحديد الكل ({items.length})</Button>
            <Button variant="outline" size="sm" onClick={clearSelection}>مسح التحديد</Button>
          </div>
        </div>

        {/* Selection summary bar */}
        <div className="flex justify-between items-center bg-gradient-to-l from-amber-100 to-yellow-50 border-2 border-amber-300 rounded-xl p-4 mt-2">
          <div>
            <div className="text-xs text-amber-900">إجمالي المُحدَّد</div>
            <div className="text-3xl font-bold text-amber-700" data-testid="bd-selected-total">
              {fmtMoney(selectedTotal)} دج
            </div>
          </div>
          <div className="text-sm text-amber-800 text-end">
            <div>عدد الوصولات المُحدَّدة: <b data-testid="bd-selected-count">{selectedIds.size}</b></div>
            <div className="text-xs text-amber-700">
              من أصل <b>{items.length}</b> وصل قابل للتحويل
            </div>
          </div>
        </div>

        {/* Entries table */}
        <div className="border rounded-xl overflow-hidden mt-2">
          {loading ? (
            <div className="p-10 text-center text-gray-500">جاري التحميل...</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              <div className="text-5xl mb-2">📭</div>
              <p>لا توجد وصولات قابلة للتحويل بالفلتر الحالي</p>
              <p className="text-xs mt-2">
                ملاحظة: يحتاج النظام إلى وجود وصولات مالية مسجلة في «التتبع المالي». استخدم زر «وصل جديد»
                داخل وحدة التتبع المالي لإنشاء بيانات اختبار.
              </p>
            </div>
          ) : (
            <div className="max-h-80 overflow-auto">
              <table className="w-full text-sm" data-testid="bd-entries-table">
                <thead className="bg-amber-50 sticky top-0">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 text-start">رقم الوصل</th>
                    <th className="p-2 text-start">تاريخ العملية</th>
                    <th className="p-2 text-start">نوع العملية</th>
                    <th className="p-2 text-start">🛎️ الخدمة</th>
                    <th className="p-2 text-start">العميل</th>
                    <th className="p-2 text-start">الموظف</th>
                    <th className="p-2 text-start">طريقة الدفع</th>
                    <th className="p-2 text-end">المبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((e) => {
                    const checked = selectedIds.has(e.id);
                    // 🆕 Prefer the operation_date from the linked daily
                    // operation (agencies think in operation-date terms);
                    // fall back to the receipt date for legacy entries.
                    const displayDate = e.operation_date || e.created_at;
                    return (
                      <tr key={e.id}
                          className={`border-t hover:bg-amber-50/40 ${checked ? 'bg-amber-50' : ''}`}
                          data-testid={`bd-row-${e.id}`}>
                        <td className="p-2">
                          <input type="checkbox" checked={checked}
                                 onChange={() => toggle(e.id)}
                                 data-testid={`bd-check-${e.id}`} />
                        </td>
                        <td className="p-2 font-mono font-bold text-xs">{e.receipt_no}</td>
                        <td className="p-2 text-xs whitespace-nowrap">{fmtDT(displayDate)}</td>
                        <td className="p-2 text-xs">{OP_TYPE_LABEL[e.operation_type] || e.operation_type}</td>
                        <td className="p-2 text-xs" data-testid={`bd-service-${e.id}`}>
                          {e.service_name || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="p-2">{e.client_name || '—'}</td>
                        <td className="p-2 text-xs">{e.employee_name || '—'}</td>
                        <td className="p-2 text-xs">{PM_LABEL[e.payment_method] || e.payment_method}</td>
                        <td className="p-2 text-end font-bold">{fmtMoney(e.amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Transfer form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <Label>اسم المُستلِم <span className="text-red-500">*</span></Label>
            <Input value={recipient} onChange={(e) => setRecipient(e.target.value)}
                   placeholder="مثال: السيد محمد — سائق التوصيل"
                   data-testid="bd-recipient" />
          </div>
          <div>
            <Label>ملاحظات</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                   placeholder="ملاحظات اختيارية"
                   data-testid="bd-notes" />
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={submitTransfer}
                  disabled={submitting || !selectedIds.size}
                  className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-40"
                  data-testid="bd-submit-transfer">
            {submitting
              ? 'جارٍ التحويل...'
              : `📤 إنشاء تحويل بـ ${fmtMoney(selectedTotal)} دج`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ════════════════════════════════════════════════════════════════════════
   TransferEntriesDialog (open from an existing transfer row)
   ════════════════════════════════════════════════════════════════════════ */
export const TransferEntriesDialog = ({ open, transferId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !transferId) return;
    setLoading(true);
    axios.get(`${API}/cash-transfers/${transferId}/entries`,
      { headers: authHeader() })
      .then((r) => setData(r.data))
      .catch((e) => toast.error('فشل تحميل تفاصيل التحويل: '
        + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [open, transferId]);

  const handlePrint = async () => {
    try {
      const r = await axios.get(
        `${API}/cash-transfers/${transferId}/print-detailed`,
        { headers: authHeader(), responseType: 'text' },
      );

      // 🛠️ Print via a Blob URL instead of document.write.
      // The old approach used:
      //   window.open('', '_blank', 'noopener,noreferrer') + document.write
      // …but `noopener` disables the returned window handle on Chrome/Edge,
      // so document.write was being silently dropped on the deployed
      // version — the user just saw "about:blank". The Blob+URL flow is
      // immune to that and also lets the browser cache + reuse the page.
      const blob = new Blob([r.data], { type: 'text/html;charset=utf-8' });
      const blobUrl = URL.createObjectURL(blob);

      // Open WITHOUT noopener/noreferrer so we keep the window handle and
      // can trigger print() once the blob has loaded.
      const w = window.open(blobUrl, '_blank');
      if (!w) {
        // Popup blocked → fall back to a same-tab download so the user
        // can still get the report (gracefully handles strict browsers).
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `transfer-${transferId}.html`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.info('🛑 المتصفح منع فتح نافذة الطباعة. تم تنزيل التقرير بدلاً من ذلك.');
        // Revoke a bit later so the browser has time to start the download.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 8000);
        return;
      }
      // Best-effort auto-trigger of print() once the blob's DOM is ready.
      // We wrap in try/catch because cross-origin policies may still
      // refuse the call — in that case the user can simply Ctrl+P.
      const triggerPrint = () => {
        try { w.focus(); w.print(); }
        catch (err) {
          console.warn('[FinancialTraceability] print() refused:', err?.message || err);
        }
      };
      try {
        w.addEventListener('load', triggerPrint, { once: true });
      } catch (_e) { /* cross-origin guard — harmless */ }
      // Hard fallback in case the `load` event already fired before we
      // attached the listener (rare but documented on Safari).
      setTimeout(triggerPrint, 1200);
      // Free the blob URL after the new tab had time to grab it.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error('فشل الطباعة: ' + (e?.response?.data?.detail || e.message));
    }
  };

  if (!open) return null;
  const t = data?.transfer || {};
  const entries = data?.entries || [];
  const deductions = data?.deductions || [];
  const deductionsTotal = data?.deductions_total || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 flex-wrap">
            <span>🧾 تفاصيل التحويل {t.reference_no || ''}</span>
            <Badge variant="outline">{entries.length} وصل</Badge>
          </DialogTitle>
          <DialogDescription>
            عرض كامل لجميع الوصولات المالية المرتبطة بهذا التحويل — تتبع غير قابل للتعديل.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="p-8 text-center text-gray-500">جاري التحميل...</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 col-span-2">
                <div className="text-xs text-amber-700">الإجمالي المرتبط</div>
                <div className="font-bold text-2xl text-amber-800" data-testid="td-linked-total">
                  {fmtMoney(data?.linked_total || t.amount)} دج
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">الوكالة</div>
                <div className="font-bold text-xs">{t.agency_name || '—'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">الحالة</div>
                <div className="font-bold">{t.status || '—'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">المُرسِل</div>
                <div className="font-bold text-xs">{t.sender_name || '—'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">المُستلِم</div>
                <div className="font-bold text-xs">{t.recipient_name || '—'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">تاريخ الإنشاء</div>
                <div className="font-bold text-xs">{fmtDT(t.created_at || t.transfer_date)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">تاريخ التأكيد</div>
                <div className="font-bold text-xs">{fmtDT(t.confirmation_date)}</div>
              </div>
            </div>

            {!data?.has_traceability && (
              <div className="border-r-4 border-orange-400 bg-orange-50 p-3 text-sm">
                <b>تنبيه:</b> هذا التحويل قديم ولا يحتوي على ربط بوصولات مالية تفصيلية.
                يمكنك إدراج التتبع التفصيلي على التحويلات الجديدة فقط.
              </div>
            )}

            {t.notes && (
              <div className="border-r-4 border-amber-400 bg-amber-50 p-3 text-sm">
                <b>ملاحظات:</b> {t.notes}
              </div>
            )}

            <div className="border rounded-xl overflow-hidden">
              <table className="w-full text-sm" data-testid="td-entries-table">
                <thead className="bg-amber-50">
                  <tr>
                    <th className="p-2 text-start">#</th>
                    <th className="p-2 text-start">رقم الوصل</th>
                    <th className="p-2 text-start">التاريخ / الوقت</th>
                    <th className="p-2 text-start">نوع العملية</th>
                    <th className="p-2 text-start">العميل</th>
                    <th className="p-2 text-start">الموظف</th>
                    <th className="p-2 text-start">طريقة الدفع</th>
                    <th className="p-2 text-end">المبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr><td colSpan={8} className="p-8 text-center text-gray-400">
                      لا توجد وصولات مرتبطة بهذا التحويل
                    </td></tr>
                  ) : entries.map((e, i) => (
                    <tr key={e.id} className="border-t hover:bg-gray-50"
                        data-testid={`td-row-${e.id}`}>
                      <td className="p-2">{i + 1}</td>
                      <td className="p-2 font-mono font-bold">{e.receipt_no}</td>
                      <td className="p-2 text-xs whitespace-nowrap">{fmtDT(e.created_at)}</td>
                      <td className="p-2 text-xs">{OP_TYPE_LABEL[e.operation_type] || e.operation_type}</td>
                      <td className="p-2">{e.client_name || '—'}</td>
                      <td className="p-2 text-xs">{e.employee_name || '—'}</td>
                      <td className="p-2 text-xs">{PM_LABEL[e.payment_method] || e.payment_method}</td>
                      <td className="p-2 text-end font-bold">{fmtMoney(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 🆕 Deductions section — refunds + cashbox expenses + granted
                discounts that reduced the agency balance during the
                window between the PREVIOUS transfer and this one. */}
            {deductions.length > 0 && (
              <div className="border rounded-xl overflow-hidden" data-testid="td-deductions-section">
                <div className="bg-rose-50 px-3 py-2 border-b border-rose-200 flex items-center justify-between">
                  <span className="font-semibold text-rose-900 text-sm">
                    💸 المصاريف والاسترجاعات في نفس الفترة ({deductions.length})
                  </span>
                  <span className="font-bold text-rose-800 text-sm">
                    -{fmtMoney(deductionsTotal)} دج
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-rose-50/50">
                    <tr>
                      <th className="p-2 text-start">#</th>
                      <th className="p-2 text-start">التاريخ</th>
                      <th className="p-2 text-start">النوع</th>
                      <th className="p-2 text-start">الوصف</th>
                      <th className="p-2 text-start">المستفيد</th>
                      <th className="p-2 text-start">المرجع</th>
                      <th className="p-2 text-end">المبلغ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deductions.map((d, i) => {
                      const typeLabels = {
                        refund: '↩️ استرجاع',
                        discount_granted: '💸 سند تخفيض',
                        airline_payment: '🛫 دفع طيران',
                        supplier_payment: '🏪 دفع مورد',
                        tickets_payment: '✈️ تسديد تذاكر',
                        other_expense: '📦 مصروف عام',
                      };
                      const ref = d.refund_operation_no || d.source_ticket_no
                        || d.source_operation_no || d.invoice_number || '—';
                      return (
                        <tr key={d.id} className="border-t hover:bg-rose-50/30"
                            data-testid={`td-deduction-${d.id}`}>
                          <td className="p-2">{i + 1}</td>
                          <td className="p-2 text-xs whitespace-nowrap">{fmtDT(d.created_at)}</td>
                          <td className="p-2 text-xs">{typeLabels[d.expense_type] || d.expense_type}</td>
                          <td className="p-2 text-xs max-w-xs truncate">{d.description || '—'}</td>
                          <td className="p-2 text-xs">{d.vendor || '—'}</td>
                          <td className="p-2 text-xs font-mono">{ref}</td>
                          <td className="p-2 text-end font-bold text-rose-700 whitespace-nowrap">
                            -{fmtMoney(d.amount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>إغلاق</Button>
          {entries.length > 0 && (
            <Button onClick={handlePrint}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="td-print-detail">
              🖨️ طباعة تقرير تفصيلي
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
